import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  getPaymentTypeTagColor,
  getVisibleDetailFields,
  formatPaymentValue,
  getTotalPaidAmount,
  COMMISSION_FIELDS,
  NEGATIVE_FIELDS,
  PAYMENT_TYPES
} from "../utils/payment-history.helpers";
import {
  CardWrapper,
  DateLabel,
  CardContainer,
  CardHeader,
  HeaderBadgesGroup,
  DueBlock,
  DueLabel,
  DueNumber,
  TypeTag,
  CampaignInfo,
  CampaignLabel,
  CampaignName,
  CampaignTooltip,
  CardBody,
  LeftColumn,
  TotalPaidLabel,
  TotalPaidAmount,
  TotalPaidCurrency,
  TotalPaidValue,
  DownloadSection,
  DownloadLabel,
  DownloadButton,
  DownloadIcon,
  RightColumn,
  DetailRow,
  DetailLabel,
  DetailAmountGroup,
  DetailCurrencyPrefix,
  DetailAmount,
  IVANote,
  Divider
} from "../styles";

const PaymentHistoryCard = ({ payment, onDownload }) => {
  const { t } = useTranslation();

  const typeId = Number(payment.issuer_payment_type_id);
  const tagColor = getPaymentTypeTagColor(typeId);
  const visibleFields = getVisibleDetailFields(typeId);
  const paymentType = PAYMENT_TYPES.find((pt) => pt.id === typeId);
  const typeName = paymentType ? t(paymentType.nameKey) : "";
  const totalPaidAmount = getTotalPaidAmount(payment);

  const formattedDate =
    payment.payment_date || payment.transaction_created_at
      ? moment(payment.payment_date || payment.transaction_created_at).format(
          "D [de] MMMM [de] YYYY"
        )
      : "";

  const getDetailAmount = (fieldName) => {
    if (!Array.isArray(payment.details)) {
      return 0;
    }
    const detail = payment.details.find((d) => d.type_id === fieldName);
    return detail ? parseFloat(detail.amount) || 0 : 0;
  };

  return (
    <CardWrapper>
      <DateLabel>
        {t("paymentHistoryCard.date")}
        {formattedDate}
      </DateLabel>
      <CardContainer>
        <CardHeader>
          <HeaderBadgesGroup>
            <DueBlock>
              <DueLabel>{t("paymentHistoryCard.dueLabel")}</DueLabel>
              <DueNumber>{payment.due || "0"}</DueNumber>
            </DueBlock>
            <TypeTag color={tagColor}>{typeName}</TypeTag>
          </HeaderBadgesGroup>
          <CampaignInfo>
            <CampaignLabel>
              {t("paymentHistoryCard.campaignLabel")}
            </CampaignLabel>
            <CampaignName
              data-tip={payment.campaign_name || ""}
              data-for={`campaign-tooltip-${payment.id}`}
            >
              {payment.campaign_name || ""}
            </CampaignName>
            <CampaignTooltip
              id={`campaign-tooltip-${payment.id}`}
              place="bottom"
              effect="solid"
            >
              {payment.campaign_name || ""}
            </CampaignTooltip>
          </CampaignInfo>
        </CardHeader>
        <CardBody>
          <LeftColumn>
            <div>
              <TotalPaidLabel>
                {t("paymentHistoryCard.totalPaidLabel")}
              </TotalPaidLabel>
              <TotalPaidAmount>
                <TotalPaidCurrency>COP $</TotalPaidCurrency>
                <TotalPaidValue>
                  {formatPaymentValue(totalPaidAmount)}
                </TotalPaidValue>
              </TotalPaidAmount>
            </div>
            <DownloadSection>
              <DownloadLabel>
                {t("paymentHistoryCard.downloadLabel")}
              </DownloadLabel>
              <DownloadButton onClick={() => onDownload && onDownload(payment)}>
                <span>{t("paymentHistoryCard.downloadButton")}</span>
                <DownloadIcon />
              </DownloadButton>
            </DownloadSection>
          </LeftColumn>
          <RightColumn>
            {visibleFields.map((fieldName, index) => {
              const amount = getDetailAmount(fieldName);
              const isNegative = NEGATIVE_FIELDS.includes(fieldName);
              const isCommission = COMMISSION_FIELDS.includes(fieldName);
              const displayAmount = formatPaymentValue(amount);

              return (
                <React.Fragment key={fieldName}>
                  {index > 0 && <Divider />}
                  <DetailRow>
                    <DetailLabel>
                      {t(`paymentHistoryCard.${fieldName}`)}
                      {isCommission && (
                        <IVANote>
                          {" "}
                          {t("paymentHistoryCard.includedIVA")}
                        </IVANote>
                      )}
                    </DetailLabel>
                    <DetailAmountGroup>
                      <DetailCurrencyPrefix>COP $</DetailCurrencyPrefix>
                      <DetailAmount negative={isNegative}>
                        {isNegative ? `- ${displayAmount}` : displayAmount}
                      </DetailAmount>
                    </DetailAmountGroup>
                  </DetailRow>
                </React.Fragment>
              );
            })}
          </RightColumn>
        </CardBody>
      </CardContainer>
    </CardWrapper>
  );
};

PaymentHistoryCard.propTypes = {
  payment: PropTypes.object.isRequired,
  onDownload: PropTypes.func
};

PaymentHistoryCard.defaultProps = {
  onDownload: null
};

export default PaymentHistoryCard;
